/**
 * PATH: packages/ui/src/kanban/components/card-detail/CardFileChip.tsx
 * FILE_ID: UI-KANBAN-CMP-CARD-FILE-CHIP-001
 * PURPOSE: ONE attachment chip for the card drawer's Files rail/panel —
 *          classified icon, name/size, primary action (open in-card
 *          editor when the host injects one, else download) + explicit
 *          secondary download / version-history buttons.
 * WHY:     ARC-2 (document layer reuse): "PDFs and images should always
 *          open inside" — the chip's primary click now opens the host's
 *          in-card editor overlay for PDF/image kinds. Download stays
 *          one explicit click away (no regression on the 2026-06-23
 *          files-rail behaviour), and chips whose bytes are QDrive-
 *          ingested expose the version rail toggle.
 * CONSTRAINT: extracted from CardFilesRail.tsx (operator 300-line
 *          ceiling — the rail kept the orchestration, this file owns the
 *          chip). Same no-email-hub-import rule: everything rides the
 *          KanbanEmailAdapter seam.
 * RELATED:
 *   - CardFilesRail.tsx: maps attachments onto these chips.
 *   - FileVersionRail.tsx: expands under the chip (panel variant).
 *   - components/email/attachmentClassify.ts: pure classifier reused here.
 */
import {
  Download,
  File,
  FileArchive,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  History,
  Loader2,
  Music,
  Presentation,
  Video,
  type LucideIcon,
} from 'lucide-react'

import { cn } from '../../../lib/utils'
import {
  classifyAttachment,
  formatFileSize,
  type AttachmentKind,
  type AttachmentPalette,
} from '../../../components/email/attachmentClassify'
import type { KanbanEmailAttachment } from '../../api'

/** kind → glyph. Mirror of email-hub's `ICON_BY_KIND` (can't import it —
 *  cycle). Same shared `AttachmentKind` keys so the glyphs stay in sync. */
const ICON_BY_KIND: Record<AttachmentKind, LucideIcon> = {
  image: FileImage,
  pdf: FileType,
  word: FileText,
  excel: FileSpreadsheet,
  powerpoint: Presentation,
  archive: FileArchive,
  audio: Music,
  video: Video,
  code: FileCode,
  text: FileText,
  email: FileText,
  other: File,
}

/** palette → Tailwind tint + icon classes (mirror of email-hub PALETTE_CSS). */
const PALETTE_CSS: Record<AttachmentPalette, { tint: string; icon: string }> = {
  sky: { tint: 'bg-sky-500/10', icon: 'text-sky-600 dark:text-sky-400' },
  rose: { tint: 'bg-rose-500/10', icon: 'text-rose-600 dark:text-rose-400' },
  blue: { tint: 'bg-blue-500/10', icon: 'text-blue-600 dark:text-blue-400' },
  emerald: { tint: 'bg-emerald-500/10', icon: 'text-emerald-600 dark:text-emerald-400' },
  amber: { tint: 'bg-amber-500/10', icon: 'text-amber-600 dark:text-amber-400' },
  violet: { tint: 'bg-violet-500/10', icon: 'text-violet-600 dark:text-violet-400' },
  slate: { tint: 'bg-slate-500/10', icon: 'text-slate-600 dark:text-slate-300' },
}

/** Map the kanban attachment onto the ui `EmailAttachment` the pure
 *  classifier expects (filename/size/mime_type are required there but
 *  optional on our wire shape — coerce with safe defaults). */
export function styleFor(att: KanbanEmailAttachment) {
  const meta = classifyAttachment({
    id: String(att.id),
    filename: att.filename ?? '',
    size: att.size ?? 0,
    mime_type: att.mime_type ?? att.content_type ?? '',
    content_type: att.content_type ?? undefined,
    extension: att.extension,
    is_image: att.is_image,
    is_pdf: att.is_pdf,
  })
  const css = PALETTE_CSS[meta.palette]
  return { Icon: ICON_BY_KIND[meta.kind] ?? File, css, meta }
}

export interface CardFileChipProps {
  att: KanbanEmailAttachment
  variant: 'rail' | 'panel'
  busy: boolean
  failed: boolean
  /** Primary click — the rail decides open-editor vs download vs
   *  jump-to-Files-tab. */
  onPrimary: () => void
  /** True when the primary action OPENS the in-card editor (chip shows
   *  "Open" affordance and the explicit download button appears). */
  opensEditor: boolean
  /** Explicit byte download (secondary when `opensEditor`). Undefined ⇒
   *  no download wired. */
  onDownload?: () => void
  /** Panel-only version history toggle (ARC-2). Undefined ⇒ hidden. */
  onToggleHistory?: () => void
  historyOpen?: boolean
}

export function CardFileChip({
  att,
  variant,
  busy,
  failed,
  onPrimary,
  opensEditor,
  onDownload,
  onToggleHistory,
  historyOpen = false,
}: CardFileChipProps) {
  const { Icon, css, meta } = styleFor(att)
  const sub = failed
    ? 'Action failed — retry'
    : `${meta.label}${att.size ? ` · ${formatFileSize(att.size)}` : ''}${opensEditor ? ' · opens in card' : ''}`
  return (
    <div
      className={cn(
        'group flex items-center gap-1 rounded-lg border pr-1.5 transition-colors',
        variant === 'rail' ? 'w-[180px] shrink-0' : 'w-full',
        failed
          ? 'border-rose-300 dark:border-rose-900/60'
          : 'border-neutral-200 hover:border-blue-400 hover:bg-blue-50/40 dark:border-zinc-800 dark:hover:border-blue-500/60 dark:hover:bg-blue-950/20',
      )}
    >
      <button
        type="button"
        onClick={onPrimary}
        disabled={busy}
        title={
          opensEditor
            ? `Open ${att.filename || 'attachment'} in card`
            : att.filename || 'attachment'
        }
        className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pl-2.5 text-left disabled:cursor-wait"
      >
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
            css.tint,
          )}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
          ) : (
            <Icon className={cn('h-3.5 w-3.5', css.icon)} aria-hidden />
          )}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[11.5px] font-medium text-neutral-700 dark:text-neutral-200">
            {att.filename || 'attachment'}
          </span>
          <span
            className={cn(
              'truncate text-[10px]',
              failed
                ? 'text-rose-600 dark:text-rose-400'
                : 'text-neutral-400 dark:text-neutral-500',
            )}
          >
            {sub}
          </span>
        </span>
      </button>
      {onDownload && (
        <button
          type="button"
          onClick={onDownload}
          disabled={busy}
          title={`Download ${att.filename || 'attachment'}`}
          aria-label={`Download ${att.filename || 'attachment'}`}
          className="rounded p-1 text-neutral-300 transition-colors hover:bg-neutral-100 hover:text-blue-500 disabled:cursor-wait dark:text-zinc-600 dark:hover:bg-zinc-800"
        >
          <Download className="h-3.5 w-3.5 shrink-0" aria-hidden />
        </button>
      )}
      {onToggleHistory && variant === 'panel' && (
        <button
          type="button"
          onClick={onToggleHistory}
          title="Version history"
          aria-label={`Version history for ${att.filename || 'attachment'}`}
          aria-expanded={historyOpen}
          className={cn(
            'rounded p-1 transition-colors hover:bg-neutral-100 dark:hover:bg-zinc-800',
            historyOpen
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-neutral-300 hover:text-blue-500 dark:text-zinc-600',
          )}
        >
          <History className="h-3.5 w-3.5 shrink-0" aria-hidden />
        </button>
      )}
    </div>
  )
}
